import * as React from 'react';
import { useState } from 'react';
import { addDays, endOfDay, endOfMinute, format, isAfter, startOfDay, subDays } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';

import { formatTimeOnly } from '@klareospace/pharos-lib-front-utils';

import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { getDateLocale } from '../../../lib/date-locale';
import { cn } from '../../../lib/utils';
import { setHoursMinutes, toTimeOfDay } from '../time-of-day-select';
import type { TimeOfDay } from '../time-of-day-select';
import { TimeScrollPicker } from '../time-scroll-picker';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DateRangePreset = 'custom' | 'today' | 'tomorrow' | 'yesterday';

export interface DateRange {
  from: Date;
  to: Date;
}

export interface DateRangeValue {
  preset: DateRangePreset;
  range: DateRange;
}

export interface DateRangeFilterProps {
  className?: string;
  /** When true, hides the presets header and keeps the picker permanently in custom-range mode — the trigger label and popover never show preset names. Default false; other consumers are unaffected. */
  customOnly?: boolean;
  label: string;
  onChange: (value: DateRangeValue) => void;
  /** Quick-select presets shown in the popover header, in order — defaults to the full set. Ignored when `customOnly` is true. */
  presets?: DateRangePreset[];
  /** When true, adds a FROM/TO time-range footer to the popover; the chip label then always includes times. */
  showTimeRange?: boolean;
  value: DateRangeValue;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PRESETS: DateRangePreset[] = ['yesterday', 'today', 'tomorrow', 'custom'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computePresetRange(preset: Exclude<DateRangePreset, 'custom'>): DateRange {
  const today = new Date();
  if (preset === 'yesterday') {
    const yesterday = subDays(today, 1);
    return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
  }
  if (preset === 'tomorrow') {
    const tomorrow = addDays(today, 1);
    return { from: startOfDay(tomorrow), to: endOfDay(tomorrow) };
  }
  return { from: startOfDay(today), to: endOfDay(today) };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DateRangeFilter({
  className,
  customOnly = false,
  label,
  onChange,
  presets = DEFAULT_PRESETS,
  showTimeRange = false,
  value,
}: DateRangeFilterProps): React.JSX.Element {
  const { t, i18n } = useTranslation('common');
  const locale = getDateLocale(i18n.language);

  const [open, setOpen] = useState(false);
  const [inCustomMode, setInCustomMode] = useState(customOnly);
  const [customFrom, setCustomFrom] = useState<Date | undefined>(undefined);

  const [stagedPreset, setStagedPreset] = useState<DateRangePreset>(customOnly ? 'custom' : value.preset);
  const [stagedFromDay, setStagedFromDay] = useState<Date>(startOfDay(value.range.from));
  const [stagedToDay, setStagedToDay] = useState<Date>(startOfDay(value.range.to));
  const [stagedFromTime, setStagedFromTime] = useState<TimeOfDay>(toTimeOfDay(value.range.from));
  const [stagedToTime, setStagedToTime] = useState<TimeOfDay>(toTimeOfDay(value.range.to));

  function isCustomPreset(preset: DateRangePreset): boolean {
    return customOnly || preset === 'custom';
  }

  function displayValueWithTimeRange(): string {
    const { preset, range } = value;
    const fromTimeLabel = formatTimeOnly(range.from);
    const toTimeLabel = formatTimeOnly(range.to);
    if (isCustomPreset(preset)) {
      return `${format(range.from, 'dd/MM')} ${fromTimeLabel} → ${format(range.to, 'dd/MM')} ${toTimeLabel}`;
    }
    return `${t(`dateRangeFilter.presets.${preset}`)} · ${fromTimeLabel} → ${toTimeLabel}`;
  }

  function displayValue(): string {
    if (showTimeRange) return displayValueWithTimeRange();
    if (inCustomMode) {
      return customFrom ? `${format(customFrom, 'dd/MM')} → ` : t('dateRangeFilter.presets.custom');
    }
    const { preset, range } = value;
    if (isCustomPreset(preset)) {
      const { from, to } = range;
      return `${format(from, 'dd/MM')} → ${format(to, 'dd/MM')}`;
    }
    return t(`dateRangeFilter.presets.${preset}`);
  }

  function handlePresetClick(preset: Exclude<DateRangePreset, 'custom'>): void {
    const range = computePresetRange(preset);
    if (showTimeRange) {
      setStagedPreset(preset);
      setStagedFromDay(startOfDay(range.from));
      setStagedToDay(startOfDay(range.to));
      setInCustomMode(false);
      setCustomFrom(undefined);
      return;
    }
    onChange({ preset, range });
    handleOpenChange(false);
  }

  function handleCustomClick(): void {
    setInCustomMode(true);
    setCustomFrom(undefined);
    if (showTimeRange) {
      setStagedPreset('custom');
    }
  }

  function handleCalendarSelect(_selected: DayPickerDateRange | undefined, clickedDay: Date): void {
    setInCustomMode(true);
    if (!customFrom) {
      setCustomFrom(clickedDay);
      return;
    }

    const earlier = customFrom <= clickedDay ? customFrom : clickedDay;
    const later = customFrom <= clickedDay ? clickedDay : customFrom;

    if (showTimeRange) {
      setStagedPreset('custom');
      setStagedFromDay(startOfDay(earlier));
      setStagedToDay(startOfDay(later));
      setCustomFrom(undefined);
      return;
    }

    onChange({ preset: 'custom', range: { from: startOfDay(earlier), to: endOfDay(later) } });
    handleOpenChange(false);
  }

  function initializeStagedState(): void {
    setStagedPreset(customOnly ? 'custom' : value.preset);
    setStagedFromDay(startOfDay(value.range.from));
    setStagedToDay(startOfDay(value.range.to));
    setStagedFromTime(toTimeOfDay(value.range.from));
    setStagedToTime(toTimeOfDay(value.range.to));
    setInCustomMode(customOnly || value.preset === 'custom');
    setCustomFrom(undefined);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen && showTimeRange) {
      initializeStagedState();
    }
    if (!nextOpen) {
      if (!customOnly) setInCustomMode(false);
      setCustomFrom(undefined);
    }
    setOpen(nextOpen);
  }

  function getCalendarSelected(): DayPickerDateRange {
    if (showTimeRange) {
      if (inCustomMode && customFrom) {
        return { from: customFrom, to: undefined };
      }
      return { from: stagedFromDay, to: stagedToDay };
    }
    if (inCustomMode) {
      return { from: customFrom, to: undefined };
    }
    return { from: value.range.from, to: value.range.to };
  }

  function isPresetActive(preset: DateRangePreset): boolean {
    if (showTimeRange) {
      if (preset === 'custom') return stagedPreset === 'custom' || inCustomMode;
      return stagedPreset === preset && !inCustomMode;
    }
    if (preset === 'custom') {
      return value.preset === 'custom' || inCustomMode;
    }
    return value.preset === preset && !inCustomMode;
  }

  function isApplyDisabled(): boolean {
    const halfPicked = inCustomMode && customFrom !== undefined;
    if (halfPicked) return true;
    const from = setHoursMinutes(stagedFromDay, stagedFromTime);
    const to = setHoursMinutes(stagedToDay, stagedToTime);
    return isAfter(from, to);
  }

  function handleApply(): void {
    const from = setHoursMinutes(stagedFromDay, stagedFromTime);
    const to = endOfMinute(setHoursMinutes(stagedToDay, stagedToTime));
    onChange({ preset: stagedPreset, range: { from, to } });
    handleOpenChange(false);
  }

  const showCalendarPane = !showTimeRange || inCustomMode;

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn('h-9 gap-1.5 rounded-md px-3 text-sm font-normal shadow-none', className)}
          type="button"
          variant="outline"
        >
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">{label}</span>
          <span>{displayValue()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <div className="flex flex-col">
          {!customOnly && (
            <div className="flex gap-1 border-b p-2">
              {presets.map((preset) => {
                return (
                  <button
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm transition-colors hover:bg-accent',
                      isPresetActive(preset) && 'bg-orange-50 font-medium text-primary',
                    )}
                    key={preset}
                    onClick={preset === 'custom' ? handleCustomClick : (): void => handlePresetClick(preset)}
                    type="button"
                  >
                    {t(`dateRangeFilter.presets.${preset}`)}
                  </button>
                );
              })}
            </div>
          )}
          {showCalendarPane && (
            <div className="p-2 w-[500px]">
              <Calendar
                captionLayout="dropdown"
                className="w-full"
                defaultMonth={showTimeRange ? stagedFromDay : value.range.from}
                locale={locale}
                mode="range"
                numberOfMonths={2}
                onSelect={handleCalendarSelect}
                selected={getCalendarSelected()}
                showOutsideDays={false}
              />
            </div>
          )}
          {showTimeRange && (
            <div className="flex w-full items-center gap-3 border-t p-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('dateRangeFilter.from')}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{format(stagedFromDay, 'dd/MM')}</span>
              <TimeScrollPicker
                ariaLabel={t('dateRangeFilter.from')}
                onChange={setStagedFromTime}
                value={stagedFromTime}
              />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('dateRangeFilter.to')}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{format(stagedToDay, 'dd/MM')}</span>
              <TimeScrollPicker ariaLabel={t('dateRangeFilter.to')} onChange={setStagedToTime} value={stagedToTime} />
              <Button className="ms-auto" disabled={isApplyDisabled()} onClick={handleApply} size="sm" type="button">
                {t('dateRangeFilter.apply')}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
